/**
 * Tiny event bus for surfacing the "install AskThePods" sheet from anywhere
 * in the app after a high-intent action (signup, first save, first topic
 * track) or when a non-installed user tries to use an app-only feature.
 *
 * Frequency rules live in useInstallPrompt — this module only fires events.
 */

const EVENT = "atp:request-install-prompt";

export type InstallTriggerReason =
  | "signup"
  | "first-save"
  | "first-track"
  | "manual"
  | "feature-blocked";

export interface InstallTriggerDetail {
  reason: InstallTriggerReason;
  /** When true, the modal opens regardless of the 7-day dismiss cooldown.
   *  Use for actions a non-installed user just tried and can't complete. */
  force?: boolean;
}

export function requestInstallPrompt(reason: InstallTriggerReason, opts?: { force?: boolean }) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENT, { detail: { reason, force: opts?.force === true } }));
}

export function onInstallPromptRequested(
  handler: (detail: InstallTriggerDetail) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<InstallTriggerDetail>).detail;
    handler({ reason: detail?.reason ?? "manual", force: detail?.force === true });
  };
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}
